/**
 * The 整理範本 the upload dialog starts on: whichever one the last accepted
 * upload from this workspace used, kept in this browser (`localStorage`).
 *
 * One key per workspace, with no account in it. Accounts on a shared browser
 * are kept apart by `clearRememberedTemplates()`, which the explicit sign-out
 * and account-deletion paths call (same contract as the hint suggestion store).
 * Transcript-only is remembered as the absence of a key.
 */

import { reportHandledException } from '@/lib/error-reporting'

const KEY_PREFIX = 'kiao.upload.lastTemplate.v1.'
const SOURCE = 'features/batch-transcription/remembered-template.ts'

/** What this module needs from `localStorage`; enumerable so sign-out can sweep it. */
export type RememberedTemplateStorage = Pick<
  Storage,
  'length' | 'key' | 'getItem' | 'setItem' | 'removeItem'
>

/** `workspace` is the active-context value: `personal` or `org:<id>`. */
export function rememberedTemplateKey(workspace: string): string {
  return `${KEY_PREFIX}${workspace}`
}

/** This browser's `localStorage`, or null where reading the property itself throws. */
export function browserTemplateStorage(): RememberedTemplateStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch (error) {
    reportHandledException(error, { source: SOURCE })
    return null
  }
}

/**
 * The remembered template, if `templates` (this workspace's loaded list) still
 * offers it. A stored value that is not a template id, or names a template the
 * list no longer has, is removed. Null means transcript-only.
 */
export function restoreRememberedTemplate(
  workspace: string,
  templates: readonly { id: number }[],
  storage: RememberedTemplateStorage | null = browserTemplateStorage()
): number | null {
  if (!storage) return null
  const key = rememberedTemplateKey(workspace)
  try {
    const raw = storage.getItem(key)
    if (raw === null) return null
    const id = Number(raw)
    const isTemplateId = raw !== '' && Number.isInteger(id) && id > 0
    if (isTemplateId && templates.some((template) => template.id === id)) return id
    storage.removeItem(key)
    return null
  } catch (error) {
    reportHandledException(error, { source: SOURCE })
    return null
  }
}

/** Remember the template an accepted upload used; null (transcript-only) forgets it. */
export function writeRememberedTemplate(
  workspace: string,
  id: number | null,
  storage: RememberedTemplateStorage | null = browserTemplateStorage()
): void {
  if (!storage) return
  const key = rememberedTemplateKey(workspace)
  try {
    if (id === null) storage.removeItem(key)
    else storage.setItem(key, String(id))
  } catch (error) {
    // A convenience only: the next dialog opens on transcript-only.
    reportHandledException(error, { source: SOURCE })
  }
}

/** Forget every workspace's remembered template. */
export function clearRememberedTemplates(
  storage: RememberedTemplateStorage | null = browserTemplateStorage()
): void {
  if (!storage) return
  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index)
      if (key?.startsWith(KEY_PREFIX)) keys.push(key)
    }
    for (const key of keys) storage.removeItem(key)
  } catch (error) {
    reportHandledException(error, { source: SOURCE })
  }
}
